import { imputationDocumentId } from '../pages/review-expense/utils/contract-expense-json';

export const ORPHAN_OPTION_PREFIX = 'orphan:';

export function categoryConditionTargets(parameters = {}) {
    if (Array.isArray(parameters.imputation_targets) && parameters.imputation_targets.length > 0) {
        return parameters.imputation_targets;
    }
    if (parameters.imputation_id || parameters.category) {
        return [parameters];
    }
    return [];
}

export function buildImputationLookup(clientImputations = []) {
    const byId = {};
    const liveIds = new Set();

    (clientImputations || []).forEach((row) => {
        const id = imputationDocumentId(row);
        if (!id) return;
        byId[id] = row;
        liveIds.add(id);
    });

    return { byId, liveIds };
}

export function isLiveImputationTarget(target, liveIds) {
    const id = target?.imputation_id;
    return !!id && liveIds.has(String(id));
}

export function orphanTargetDisplayLabel(target, orphanSuffix) {
    const path = target.parent_category && target.imputation_level === 'child'
        ? `${target.parent_category} / ${target.category}`
        : (target.category || target.imputation_id);
    return `${path} (${orphanSuffix})`;
}

export function legacyOrphanOptionValue(target) {
    const category = target?.category || 'unknown';
    const parent = target?.parent_category || '';
    return `${ORPHAN_OPTION_PREFIX}legacy:${parent}:${category}`;
}

export function buildOrphanSelectOption(target, { orphanLabel, optionValue }) {
    return {
        value: optionValue,
        label: orphanTargetDisplayLabel(target, orphanLabel),
        isOrphan: true,
        category: target.category,
        imputation_id: target.imputation_id ? String(target.imputation_id) : null,
        imputation_level: target.imputation_level,
        parent_imputation_id: target.parent_imputation_id,
        parent_category: target.parent_category,
    };
}

export function resolveImputationSelectOption(target, optionsById, { orphanLabel }) {
    const id = target?.imputation_id ? String(target.imputation_id) : null;
    if (id && optionsById[id]) {
        return optionsById[id];
    }
    if (id) {
        return buildOrphanSelectOption(target, {
            orphanLabel,
            optionValue: `${ORPHAN_OPTION_PREFIX}${id}`,
        });
    }
    if (target?.category) {
        return buildOrphanSelectOption(target, {
            orphanLabel,
            optionValue: legacyOrphanOptionValue(target),
        });
    }
    return null;
}

export function resolveImputationSelectOptions(parameters, optionsById, { orphanLabel }) {
    const targets = categoryConditionTargets(parameters);
    return targets
        .map(target => resolveImputationSelectOption(target, optionsById, { orphanLabel }))
        .filter(Boolean);
}

export function categoryTargetSummaryLabel(target, { byId, liveIds, orphanLabel }) {
    const id = target?.imputation_id ? String(target.imputation_id) : null;
    if (id && liveIds.has(id) && byId[id]) {
        const row = byId[id];
        const parentId = row.parent_id ? String(row.parent_id) : null;
        if (parentId && byId[parentId]) {
            return `${byId[parentId].category} / ${row.category}`;
        }
        return row.category;
    }

    return orphanTargetDisplayLabel(target, orphanLabel);
}

export function hasInvalidCategorySpendLimitTargets(conditions, scope, liveIds) {
    return (conditions || []).some((condition) => {
        if (scope !== 'expense' || !['category_spend_limit', 'category_match'].includes(condition?.type)) {
            return false;
        }
        const targets = categoryConditionTargets(condition.parameters || {});
        if (targets.length === 0) {
            return false;
        }
        return !targets.every(target => isLiveImputationTarget(target, liveIds));
    });
}
